// Lightweight, ReDoS-safe validators and sanitizers for user input

// Remove control characters, trim, collapse whitespace, and optionally cap length.
export function sanitizePlainText(input: string, maxLen?: number): string {
  if (typeof input !== 'string') return '';
  let s = input
    .replace(/[\u0000-\u001F\u007F]/g, ' ') // control chars to space
    .replace(/[<>]/g, '') // strip angle brackets to reduce script injection in downstream consumers
    .replace(/\s+/g, ' ') // collapse whitespace
    .trim();
  if (typeof maxLen === 'number' && maxLen > 0 && s.length > maxLen) {
    s = s.slice(0, maxLen);
  }
  return s;
}

export function safeTrim(input: string, maxLen: number): string {
  return sanitizePlainText(String(input || ''), maxLen);
}

// Names: allow letters (including diacritics), spaces, hyphens, apostrophes.
// Length between 2 and 50 by default.
export function isValidName(name: string, min = 2, max = 50): boolean {
  const s = sanitizePlainText(name);
  if (s.length < min || s.length > max) return false;
  // Unicode letters and allowed punctuation
  const re = /^[\p{L}][\p{L}\s\-']*[\p{L}]$/u;
  return re.test(s);
}

// Simple, safe email regex (non-backtracking heavy)
export function isValidEmail(email: string, maxLen = 254): boolean {
  const s = sanitizePlainText(email, maxLen);
  if (!s) return false;
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  return re.test(s);
}

// Dutch numbers: mobile 06xxxxxxxx; landline 0X or +31/0031 forms; spaces/dashes permitted.
export function isValidDutchPhone(phone: string): boolean {
  const clean = String(phone || '').replace(/[\s\-]/g, '');
  const mobile = /^(\+31|0031|0)6\d{8}$/;
  const landline = /^(\+31|0031|0)[1-9]\d{7,8}$/;
  return mobile.test(clean) || landline.test(clean);
}

// Clamp strings before including them in outbound messages
export function clamp(input: string, maxLen: number): string {
  if (typeof input !== 'string') return '';
  return input.length > maxLen ? input.slice(0, maxLen) : input;
}
